import React from 'react';
import { useNavigate } from 'react-router';
import {
  FREE_TIER,
  skipWelcome,
  useDetectionUpdates,
  useDeviceUpdates,
  useFirstRun,
  useFirstRunReporter,
  useRequestAgentDownload,
  type AgentPlatform,
  type FirstRunStep,
} from '@redbax/shared';
import { Button, SoloSpider, Stepper } from '@redbax/ui-kit';
import { Check, Copy, Download, Loader2, Terminal } from 'lucide-react';
import { OnboardingShell } from './OnboardingShell';

/**
 * The guided first run for a free individual (epic #121).
 *
 * Three steps, and the last one is the point: install the agent, sign in from that machine, then
 * watch Redbax catch something. Without the third, someone has installed software and is looking at an
 * empty dashboard — which is where this journey used to end.
 *
 * THE STEP IS DERIVED, never stored (see useFirstRun). The page advances because the server said a
 * device came online or a detection arrived, not because a button was pressed, so closing the tab
 * mid-install loses nothing.
 */

type Platform = Extract<AgentPlatform, 'windows' | 'linux'>;

const STEP_INDEX: Record<FirstRunStep, number> = {
  install: 1,
  connect: 2,
  verify: 3,
  complete: 3,
};

const STEP_LABEL: Record<FirstRunStep, string> = {
  install: 'Install the agent',
  connect: 'Sign in from that machine',
  verify: 'See it work',
  complete: 'Done',
};

/**
 * How each platform is installed.
 *
 * TWO DIFFERENT SHAPES ON PURPOSE, because the platforms are different.
 *
 * Linux gets a single public line, the shape Tailscale and Datadog use. It carries no
 * credential — the agent is inert until a signed-in human approves the device — so it is short
 * enough to retype onto a second machine, which is the common case: the dashboard is usually
 * open on a different computer from the one being protected.
 *
 * Windows gets NO command at all. The MSI installs the service and the tray, and the tray
 * prompts to sign in; a `msiexec` line was friction invented for a silent fleet install that an
 * individual developer is not doing. Download and double-click, as Vanta and Drata do.
 */
const INSTALL: Record<Platform, { label: string; command: string | null; after: string }> = {
  linux: {
    label: 'Linux',
    command: 'curl -fsSL https://get.redbax.io | sudo sh',
    after: 'It verifies the download, installs the agent, and opens a browser so you can approve this machine.',
  },
  windows: {
    label: 'Windows',
    command: null,
    after: 'Run the installer, then click the Redbax icon in your system tray when it asks you to sign in.',
  },
};

/** The standard Visa test number. Never a real card, and it matches the seeded PCI rule exactly. */
const TEST_CARD = '4111 1111 1111 1111';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'linux';
  const ua = navigator.userAgent;
  if (/Windows/i.test(ua)) return 'windows';
  // Everything else lands on Linux: macOS has no build yet, so offering it would be a lie, and a Mac
  // user is better served by seeing that said plainly than by a download that 404s.
  return 'linux';
}

export function WelcomePage() {
  const navigate = useNavigate();
  const { step, device, isLoading } = useFirstRun();
  const report = useFirstRunReporter();

  // Subscribed for their side effect: both hooks patch the query cache that useFirstRun reads, so the
  // page advances on a push rather than on a poll.
  useDeviceUpdates();
  useDetectionUpdates();

  const [platform, setPlatform] = React.useState<Platform>(detectPlatform);

  // Once per mount, not per render: the server counts occurrences, and a figure that moved with React
  // would describe the component rather than the person. Someone arriving already finished is not a
  // new start — the completion effect below is the only thing that should fire for them.
  const started = React.useRef(false);
  React.useEffect(() => {
    if (started.current || isLoading || step === 'complete') return;
    started.current = true;
    report('welcome_started');
  }, [isLoading, report, step]);

  // Reported when the flow actually reaches the end, which happens because a detection arrived — a
  // server fact, not a button press.
  const completed = React.useRef(false);
  React.useEffect(() => {
    if (step !== 'complete' || completed.current) return;
    completed.current = true;
    report('welcome_completed');
  }, [step, report]);
  const [copied, setCopied] = React.useState<string | null>(null);
  const requestDownload = useRequestAgentDownload();

  const copy = async (text: string, key: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
  };

  const download = async () => {
    try {
      report('agent_downloaded');
      const { url } = await requestDownload.mutateAsync(platform);
      const link = document.createElement('a');
      link.href = url;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      // surfaced below
    }
  };

  const finish = () => navigate('/', { replace: true });

  const skip = () => {
    skipWelcome();
    finish();
  };

  // Nothing is decided until both queries have answered, or the page flashes step 1 at someone who is
  // already finished.
  if (isLoading) {
    return (
      <OnboardingShell title="Getting things ready…" wide>
        <div className="flex items-center gap-3 text-sm text-text-secondary">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking your devices
        </div>
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell
      title={step === 'complete' ? "You're protected" : 'Welcome to Redbax'}
      subtitle={
        step === 'complete'
          ? 'That is the whole product working. Everything from here happens on your dashboard.'
          : `Two or three minutes, and your machine is covered. Free accounts include ${FREE_TIER.deviceLimit} devices.`
      }
      wide
    >
      <Stepper current={STEP_INDEX[step]} total={3} label={STEP_LABEL[step]} />

      {step === 'install' && (
        <div className="space-y-5">
          <div className="flex gap-2">
            {(Object.keys(INSTALL) as Platform[]).map((p) => (
              <Button
                key={p}
                variant={platform === p ? 'default' : 'secondary'}
                size="sm"
                onClick={() => setPlatform(p)}
              >
                {INSTALL[p].label}
              </Button>
            ))}
            <span className="self-center text-xs text-text-secondary">macOS — coming soon</span>
          </div>

          {INSTALL[platform].command ? (
            <CodeBlock
              label="Run this on the machine you want to protect"
              code={INSTALL[platform].command!}
              copied={copied === 'command'}
              onCopy={() => copy(INSTALL[platform].command!, 'command')}
              note={INSTALL[platform].after}
            />
          ) : (
            <>
              <Button onClick={download} disabled={requestDownload.isPending}>
                {requestDownload.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Download for {INSTALL[platform].label}
              </Button>
              {requestDownload.isError && (
                <p className="text-sm text-red-accent">
                  Couldn&rsquo;t start the download. Please try again.
                </p>
              )}
              <p className="text-sm text-text-secondary">{INSTALL[platform].after}</p>
            </>
          )}
        </div>
      )}

      {step === 'connect' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-sm text-text-primary">
            <Loader2 className="h-4 w-4 animate-spin text-red-accent" />
            Waiting for {device?.hostname ?? 'your device'} to come online…
          </div>
          <p className="text-sm text-text-secondary">
            {INSTALL[platform].after} Approve it there and this page will move on by itself.
          </p>
        </div>
      )}

      {step === 'verify' && (
        <div className="space-y-5">
          <p className="text-sm text-text-secondary">
            <span className="text-text-primary">{device?.hostname}</span> is online and inspecting
            traffic to AI tools. Let&rsquo;s prove it.
          </p>

          <CodeBlock
            label="Open ChatGPT, paste this, and send it"
            code={TEST_CARD}
            copied={copied === 'card'}
            onCopy={() => copy(TEST_CARD, 'card')}
            note="A well-known test card number — never a real one. Redbax should block it before it leaves your machine."
          />

          <div className="flex items-center gap-3 text-sm text-text-secondary">
            <Loader2 className="h-4 w-4 animate-spin text-red-accent" />
            Watching for the detection…
          </div>
        </div>
      )}

      {step === 'complete' && (
        <div className="space-y-5">
          <div className="flex items-start gap-3 rounded-lg border border-red-accent/30 bg-red-subtle/30 p-4">
            <Check className="mt-0.5 h-5 w-5 shrink-0 text-red-accent" />
            <div>
              <p className="text-sm font-medium text-text-primary">
                Caught and blocked before it left your machine.
              </p>
              <p className="mt-1 text-sm text-text-secondary">
                That is what Redbax does for every AI tool your devices talk to, using the default
                detection policy. Your free account keeps {FREE_TIER.retentionDays} days of history.
              </p>
            </div>
          </div>
          <div className="flex justify-center py-2">
            <SoloSpider size={64} />
          </div>
          <Button onClick={finish}>Go to your dashboard</Button>
        </div>
      )}

      {step !== 'complete' && (
        <div className="mt-6 border-t border-border pt-4">
          <button
            type="button"
            onClick={() => {
              report('welcome_skipped');
              skip();
            }}
            className="text-sm text-text-secondary hover:text-text-primary"
          >
            I&rsquo;ll do this later
          </button>
          <p className="mt-1 text-xs text-text-secondary">
            Your dashboard will be empty until a device is connected.
          </p>
        </div>
      )}
    </OnboardingShell>
  );
}

function CodeBlock({
  label,
  code,
  note,
  copied,
  onCopy,
}: {
  label: string;
  code: string;
  note?: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-text-secondary">{label}</label>
      <div className="flex items-start gap-2 rounded-lg bg-surface-raised p-3 font-mono text-xs">
        <Terminal className="mt-0.5 h-4 w-4 shrink-0 text-text-secondary" />
        <code className="flex-1 whitespace-pre-wrap break-all text-text-primary">{code}</code>
        <Button variant="ghost" size="sm" onClick={onCopy} className="shrink-0">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
      {note && <p className="text-xs text-text-secondary">{note}</p>}
    </div>
  );
}
